import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { 
  FaFacebook, 
  FaSpotify, 
  FaLinkedin, 
  FaInstagram, 
  FaYoutube, 
  FaEnvelope, 
  FaPhone, 
  FaMapMarkerAlt 
} from 'react-icons/fa';

const FooterContainer = styled.footer`
  background: #ffffff;
  color: #3730A3;
`;

const TopFooter = styled.div`
  background: #ffffff;
  padding: 3rem 5%;
`;

const BottomFooter = styled.div`
  font-family: 'Times New Roman', serif;
  background: #3730A3;
  padding: 2rem 5%;
  text-align: center;
  font-size: 1.0rem;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;

  p {
    margin: 0;
    white-space: nowrap;
  }

  @media (max-width: 768px) {
    p {
      white-space: normal;
    }
  }
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1.3fr 1fr 1fr 1fr 1fr;
  gap: 2rem;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  }
`;

const FooterSection = styled.div`
  h3 {
    font-family: 'Times New Roman', serif;
    color: #3730A3;
    margin-bottom: 1.5rem;
    font-size: 1.2rem;
    position: relative;
    
    &:after {
      content: '';
      position: absolute;
      bottom: -8px;
      left: 0;
      width: 40px;
      height: 2px;
      background: #a78bfa;
    }
  }
  
  p {
    font-family: 'Times New Roman', serif;
    color: #4a4a6a;
    line-height: 1.6;
    margin-bottom: 1rem;
  }
`;

const FooterLinks = styled.ul`
  list-style: none;
  padding: 0;
`;

const FooterLink = styled.li`
  font-family: 'Times New Roman', serif;
  margin-bottom: 0.8rem;
  
  a {
    font-family: 'Times New Roman', serif;
    color: #4a4a6a;
    text-decoration: none;
    transition: all 0.3s ease;
    font-size: 0.95rem;
    
    &:hover {
      color: #3730A3;
      padding-left: 8px;
    }
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
  
  a {
    color: #3730A3;
    font-size: 1.5rem;
    transition: all 0.3s ease;
    
    &:hover {
      color: #a78bfa;
      transform: translateY(-3px);
    }
  }
`;

const ContactInfo = styled.div`
  font-family: 'Times New Roman', serif;
  display: flex;
  align-items: flex-start;
  gap: 0.8rem;
  margin-bottom: 1.2rem;
  color: #4a4a6a;
  
  svg {
    color: #3730A3;
    font-size: 1.2rem;
    margin-top: 3px;
    flex-shrink: 0;
  }
  
  span {
    line-height: 1.5;
  }
`;

const LogoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.1rem;
  margin-bottom: 1.2rem;
  
  img {
    height: 40px;           /* 👈 Increase from 40px */
    width: auto;
    filter: none;
  }

  .new-logo {
    height: 75px;           /* 👈 Increase from 50px */
    width: auto;
  }

  @media (max-width: 768px) {
    gap: 1rem;
    
    img {
      height: 25px;         /* 👈 Increase from 30px */
    }
    
    .new-logo {
      height: 30px;         /* 👈 Increase from 38px */
    }
  }
`;

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <FooterContainer>
      <TopFooter>
        <Container>
          {/* About Section with Both Logos */}
          <FooterSection>
            <LogoRow>
              {/* New Logo */}
              <img 
                className="new-logo"
                src="/images/new-logo.png" 
                alt="New Logo"
              />
              {/* Existing Logo */}
              <img 
                src="/images/logo.png" 
                alt="Learn Thru Us Academy"
                style={{ filter: 'brightness(0) saturate(100%) invert(11%) sepia(53%) saturate(5613%) hue-rotate(245deg) brightness(94%) contrast(103%)' }}
              />
            </LogoRow>
            <p>Empowering healthcare professionals with industry-leading coding and documentation training since 2008.</p>
            <p style={{ fontStyle: 'italic', color: '#3730A3' }}>"Your success is our mission. We don't just teach medical coding; we build careers."</p>
            <SocialLinks>
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer"><FaFacebook /></a>
              <a href="https://spotify.com" target="_blank" rel="noopener noreferrer"><FaSpotify /></a>
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer"><FaLinkedin /></a>
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer"><FaInstagram /></a>
              <a href="https://youtube.com" target="_blank" rel="noopener noreferrer"><FaYoutube /></a>
            </SocialLinks>
          </FooterSection>

          {/* Quick Links - Main Pages from Header */}
          <FooterSection>
            <h3>Quick Links</h3>
            <FooterLinks>
              <FooterLink><Link to="/">Home</Link></FooterLink>
              <FooterLink><Link to="/about-us">About LTU</Link></FooterLink>
              <FooterLink><Link to="/our-trainers">About Trainer</Link></FooterLink>
              <FooterLink><Link to="/faq">FAQ</Link></FooterLink>
              <FooterLink><Link to="/testimonials">Testimonials</Link></FooterLink>
            </FooterLinks>
          </FooterSection>

          {/* Speciality Courses from Header Dropdown */}
          <FooterSection>
            <h3>Speciality Courses</h3>
            <FooterLinks>
              <FooterLink><Link to="/ccs">CCS</Link></FooterLink>
              <FooterLink><Link to="/cdi">CDI</Link></FooterLink>
              <FooterLink><Link to="/basic-coding">Basic Coding Training</Link></FooterLink>
              <FooterLink><Link to="/advanced-coding">Advanced Coding Training</Link></FooterLink>
              <FooterLink><Link to="/specialty/ed-em-training">ED / E&M Training</Link></FooterLink>
              <FooterLink><Link to="/surgery-coding">Surgery Coding</Link></FooterLink>
              <FooterLink><Link to="/inpatient-drg">Inpatient DRG</Link></FooterLink>
            </FooterLinks>
          </FooterSection>

          {/* More Section - Gallery, Jobs, Blog, Contact */}
          <FooterSection>
            <h3>More</h3>
            <FooterLinks>
              <FooterLink><Link to="/gallery">Hall of Achievers</Link></FooterLink>
              <FooterLink><Link to="/job-opportunities">Job Opportunities</Link></FooterLink>
              <FooterLink><Link to="/blog">Blog</Link></FooterLink>
              <FooterLink><Link to="/contact-us">Contact Us</Link></FooterLink>
              <FooterLink><Link to="/newsletter-signup">Subscribe for Updates</Link></FooterLink>
              <FooterLink><Link to="/internal-events">Internal Events</Link></FooterLink>
              <FooterLink><Link to="/external-events">External Events</Link></FooterLink>
            </FooterLinks>
          </FooterSection>

          {/* Contact Information */}
          <FooterSection>
            <h3>Contact Us</h3>
            <ContactInfo>
              <FaMapMarkerAlt />
              <span>8-3-323, Unit No. 524, 4th Floor, Vasavi MPM Grand, Yellareddyguda,<br />Ameerpet, Hyderabad, Telangana - 500 073</span>
            </ContactInfo>
            <ContactInfo>
              <FaPhone />
              <span>+1 (888) 123-4567</span>
            </ContactInfo>
            <ContactInfo>
              <FaEnvelope />
              <span>connect@learnthruus.com</span>
            </ContactInfo>
          </FooterSection>
        </Container>
      </TopFooter>
      
      <BottomFooter>
          <p>© {currentYear} Learn Thru Us Academy. All rights reserved.</p>
      </BottomFooter>
    </FooterContainer>
  );
};

const MobileFooterStyles = styled.div`
  @media screen and (max-width: 768px) {

    ${TopFooter} {
      padding: 2rem 4% !important;
    }

    ${Container} {
      grid-template-columns: repeat(2, 1fr) !important;
      gap: 1.5rem !important;
    }

    ${FooterSection} {
      text-align: left !important;
    }

    ${FooterSection} h3 {
      font-size: 1rem !important;
      margin-bottom: 1rem !important;
    }

    ${FooterSection} p {
      font-size: 0.85rem !important;
    }

    ${FooterLink} a {
      font-size: 0.85rem !important;
    }

    ${ContactInfo} {
      font-size: 0.85rem !important;
    }

    ${SocialLinks} {
      justify-content: flex-start !important;
    }

    ${LogoRow} {
      justify-content: flex-start !important;
    }

    ${BottomFooter} {
      padding: 1.5rem 4% !important;
    }

    ${BottomFooter} ${Container} {
      grid-template-columns: 1fr !important;
      text-align: center !important;
    }

    ${BottomFooter} ${Container} p {
      font-size: 0.85rem !important;
      line-height: 1.5 !important;
    }

  }
`;

export default function FooterWrapper() {
  return (
    <MobileFooterStyles>
      <Footer />
    </MobileFooterStyles>
  );
}